/**
 * **舞台の設定（グローバル 1 枠）**（Issue #535。索引 #525。設計文書 §6 / §6.1）。
 * **`duelEditor/` の外**。
 *
 * カメラも Field も**舞台が 1 つ持つ**ものなので、オブジェクトレイヤーには入れない
 * （ユーザー判断 2026-09-25）。演出が書き換え、**消す側の演出が `'reset'` する**。
 *
 * ## 宣言であって描画中の関数呼び出しではない
 *
 * 描画中に書き換えるとシークで再現しない（同じ入力なら同じ絵、が破れて書き出しとプレビューがずれる）。
 * 整列後の帯を**時刻順に 1 回**畳んで「フレーム t の設定」を出す。
 *
 * ## 枠が 1 つなので漏れの種類も 1 つ
 *
 * `fx_objects` のような一覧の診断は要らず、「**最後まで `reset` されない**」だけを見る。
 *
 * **ここは台本を作る層**（そのフレームの設定の値まで。Issue #766）。値 → カメラ・CSS の filter の写し方は描く側の
 * `fxScenePicture.js`（`sceneShot` / `gradeFilter` / `boardFilter`）。
 */
import { evaluateAt } from '../duelEditor/core/effect';
import { shake as shakeAt } from '../duelEditor/core/effect/motion';
import { SHOTS } from '../duelEditor/stage/camera';   // ショットの名前の表（名乗った名前を検めるだけ）

export const FX_SCENE = 'fx_scene';

/**
 * 舞台の素の設定（`reset` で戻る先）。**`shot: null` は「ページのカメラのまま」**
 * —— 以前は `'live'` で、演出が何も要求していないときもページのカメラを実写のショットで上書きしていた
 * （ページで既定のカメラを変えても効かなかった）。
 */
export const DEFAULT_SCENE = Object.freeze({ field: 'live', shot: null, camera: null });

const num = (v, def = 0) => (Number.isFinite(v) ? v : def);

/** 演出が名乗った `scene` を、設定の並びに揃える（`'reset'` はそのまま印として残す） */
function normalizeScene(scene, ctx) {
  const v = typeof scene === 'function' ? scene(ctx) : scene;
  if (v == null) return [];
  if (v === 'reset') return [{ ms: 0, reset: true }];
  // 並びの中の `'reset'` / `{ ms, reset: true }` は**その時刻に戻す**（Issue #544）
  return (Array.isArray(v) ? v : [v]).map((cfg) => (cfg === 'reset' ? { ms: 0, reset: true } : { ...cfg, ms: num(cfg?.ms) }));
}

/**
 * 帯の一覧 → **舞台の設定の区切り**（時刻順）。
 *
 * @param objects 整列後の帯
 * @param effectOf 帯の型の id → 演出
 * @returns `{ steps, issues }`。`steps` は `[{ ms, scene }]`（`scene` はそのフレームの設定）
 */
export function buildFxScene(objects = [], effectOf = () => null, contextOf = () => ({})) {
  const marks = [];
  (objects ?? []).forEach((clip) => {
    if (clip?.hidden) return;
    const effect = effectOf(clip.type);
    if (!effect?.scene) return;
    const ctx = { ...contextOf(clip), payload: clip.payload ?? {} };
    normalizeScene(effect.scene, ctx).forEach((cfg) => {
      marks.push({ ms: num(clip.startMs) + cfg.ms, cfg, by: clip.type, endMs: num(clip.endMs, num(clip.startMs)) });
    });
  });
  // 同じ時刻は `reset` を先に（戻したうえで新しい設定を当てる）
  marks.sort((a, b) => a.ms - b.ms || ((a.cfg.reset ? 0 : 1) - (b.cfg.reset ? 0 : 1)));

  const steps = [{ ms: 0, scene: DEFAULT_SCENE }];
  const issues = [];
  let cur = DEFAULT_SCENE;
  let openedBy = null;
  marks.forEach((m) => {
    if (m.cfg.reset) {
      if (cur !== DEFAULT_SCENE) { cur = DEFAULT_SCENE; openedBy = null; steps.push({ ms: m.ms, scene: cur }); }
      return;
    }
    const { ms, reset, shake, grade, split, ...rest } = m.cfg;
    // **未知の shot は素のまま**（綴り間違いで画面が飛ばない）
    const shot = rest.shot && SHOTS[rest.shot] ? rest.shot : cur.shot;
    /*
     * **揺れは起きた時刻を持つ**（Issue #541）。減衰して 0 に戻るので `reset` は要らず、
     * **揺れだけの要求は「舞台を変えた」に数えない**（`scene_not_reset` を出さない）。
     */
    const next = { ...cur, ...rest, shot };
    // **カメラの数値は設定した時刻から数える**（後から揺れだけの区切りが来ても頭に戻らない。Issue #544）
    if (rest.camera) next.cameraAt = m.ms;
    if (shake) next.shake = { ...shake, startMs: m.ms, seed: `${m.by}:${m.ms}` };
    // **色の調整は帯の間だけ**（Issue #549。インパクトフレームなど一瞬のもの。`reset` は要らない）
    if (grade) next.gradeCfg = { ...grade, startMs: m.ms, endMs: m.endMs };
    // **画面を切ってずらす**も帯の間だけ（Issue #553。ずれが 0 に戻れば何もしない。`reset` は要らない）
    if (split) next.splitCfg = { ...split, startMs: m.ms, endMs: m.endMs };
    cur = Object.freeze(next);
    if (Object.keys(rest).length) openedBy = m.by;
    steps.push({ ms: m.ms, scene: cur });
  });
  if (openedBy) {
    issues.push({
      code: 'scene_not_reset',
      level: 'warn',
      message: `${openedBy} が変えた舞台の設定が最後まで reset されていない`,
    });
  }
  return { steps: Object.freeze(steps), issues: Object.freeze(issues) };
}

/** そのフレームの設定（**キーフレームはここで解く**。`cam/<prop>` は数値） */
export function fxSceneAt(frameMs, built) {
  const ms = num(frameMs);
  const steps = built?.steps ?? [];
  let hit = DEFAULT_SCENE;
  let at = 0;
  for (let i = 0; i < steps.length; i += 1) {
    if (steps[i].ms > ms) break;
    hit = steps[i].scene;
    at = steps[i].ms;
  }
  const shakeOffset = shakeOf(hit.shake, ms);
  const grade = gradeOf(hit.gradeCfg, ms);
  const split = splitOf(hit.splitCfg, ms);
  if (!hit.camera && !shakeOffset && !grade && !split) return hit;
  // カメラの数値は**カメラを設定した区切りの頭からの経過**で解く（帯のローカル時間と同じ扱い）
  const local = ms - (Number.isFinite(hit.cameraAt) ? hit.cameraAt : at);
  let camera = null;
  if (hit.camera) {
    camera = {};
    Object.entries(hit.camera).forEach(([k, v]) => { camera[k] = evaluateAt(v, local); });
  }
  return { ...hit, camera, shakeOffset, grade, split };
}

/**
 * **画面を切ってずらす**（Issue #553）。そのフレームの値 → `{ at, nudge, angle, offset, gap }`。素のままなら null。
 *
 * 切る線は `at`（舞台のピクセル `[x, y]` か、舞台が解く場所の宣言＝デュエルなら `Anchor`）を通り、
 * 向きは `angle`（度。舞台のピクセルで y 下向き、0 = 水平で右向き）。線の**片側を線に沿って `-offset`、反対側を `+offset`** ずらし、
 * `gap` だけ線から離す（両側で合わせて `gap`。離れたところは下の層が見える）。
 * `angle` / `offset` / `gap` はキーフレームも取れる（切った時刻からの経過で解く）。**帯の間だけ**効く。
 * `at` を解くのは舞台の仕事（ここは場所の意味を知らない）。
 */
export function splitOf(split, ms) {
  if (!split) return null;
  const t = num(ms);
  if (t < num(split.startMs) || (Number.isFinite(split.endMs) && t >= split.endMs)) return null;
  const local = t - num(split.startMs);
  const offset = num(evaluateAt(split.offset, local));
  const gap = Math.max(0, num(evaluateAt(split.gap, local)));
  if (Math.abs(offset) < 0.01 && gap < 0.01) return null;
  const nudge = Array.isArray(split.nudge) ? [num(split.nudge[0]), num(split.nudge[1])] : [0, 0];
  return { at: split.at ?? null, nudge, angle: num(evaluateAt(split.angle, local)), offset, gap };
}

/**
 * 色の調整の素の値（**この値なら何も掛けない**）。
 * `boardGrayscale` だけは舞台全体ではなく**盤の側の層**（背景・盤＝実写・置かれたカード）にだけ掛ける白黒
 * （HUD と演出の層は色のまま。決着の「止まって色が抜ける」。Issue #604）
 */
export const GRADE_BASE = Object.freeze({
  brightness: 1, contrast: 1, saturate: 1, grayscale: 0, invert: 0, hue: 0, bloom: 0, chroma: 0, boardGrayscale: 0,
});

/**
 * **そのフレームの色の調整**（帯の間だけ。キーフレームは帯の頭からの経過で解く）。素のままなら null。
 * `bloom` / `chroma` は演出の層の後処理に**足す**量、残りは舞台全体の CSS の filter。
 */
export function gradeOf(grade, ms) {
  if (!grade) return null;
  const t = num(ms);
  if (t < num(grade.startMs) || (Number.isFinite(grade.endMs) && t >= grade.endMs)) return null;
  const local = t - num(grade.startMs);
  const out = { ...GRADE_BASE };
  let changed = false;
  Object.keys(GRADE_BASE).forEach((k) => {
    if (grade[k] === undefined) return;
    const v = evaluateAt(grade[k], local);
    if (Number.isFinite(v)) { out[k] = v; if (v !== GRADE_BASE[k]) changed = true; }
  });
  return changed ? out : null;
}

/**
 * **揺れ → カメラのずらし** `[dx, dy]`（ワールドの単位。0 なら null）。
 * `ms` からの決定論的なノイズ（`motion.shake`）なので、同じ時刻なら同じ揺れ ＝ 書き出しと揃う。
 * `amp` / `freq` / `decayMs` はキーフレームも取れる（揺れ始めからの経過で解く）。
 */
export function shakeOf(shake, ms) {
  if (!shake) return null;
  const local = num(ms) - num(shake.startMs);
  if (local < 0) return null;
  const opt = {
    amp: num(evaluateAt(shake.amp, local), 12),
    freq: num(evaluateAt(shake.freq, local), 18),
    decayMs: num(evaluateAt(shake.decayMs, local), 400),
  };
  const [dx, dy] = shakeAt(shake.seed ?? 'shake', local, opt);
  return dx === 0 && dy === 0 ? null : [dx, dy];
}

/** 状態チャンネルにする（`build(anchors, ctx, objects)`） */
export function fxSceneChannel({ effectOf, contextOf }) {
  return {
    id: FX_SCENE,
    label: '舞台の設定（カメラ・Field）',
    kind: 'build',
    // context は帯ごとに引く（`anchors` と `ctx` から盤面・履歴を組む。Issue #541）
    build: (anchors, ctx, objects) => buildFxScene(objects, effectOf, (clip) => (contextOf ? contextOf(clip, { anchors, ctx }) : {})),
    at: (ms, built) => fxSceneAt(ms, built),
  };
}
